import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useSelector } from '@algoan/dashboard/store/store';
import { CustomerFilter } from './customer-filter';
import { appConfigLoadingSelector, isFilteringSelector } from '@algoan/dashboard/store/selectors';
import { Spinner } from '../../shared-components/atomic-components';
import { messages } from '../application/application-messages';
import {
  customerListErrorSelector,
  customerListSelector,
  isCustomerListLoadingSelector,
  isSortingSelector,
} from './customer-table-selectors';
import { ActionSection } from './action-section';
import { CustomerDataTable } from './customer-data-table';

export const CustomerTable: React.FC = () => {
  const customerList = useSelector(customerListSelector);
  const areCustomersFetching = useSelector(isCustomerListLoadingSelector);
  const error = useSelector(customerListErrorSelector);
  const isSorting = useSelector(isSortingSelector);
  const isFiltering = useSelector(isFilteringSelector);
  const isAppConfigLoading = useSelector(appConfigLoadingSelector);
  const intl = useIntl();

  const isLoadingIconVisible = (): boolean => {
    if (isAppConfigLoading) return true;
    if (!areCustomersFetching) return false;

    /* When we are sorting or filtering, we don't want to display this icon
    since they is already a loading overlay in this case.
    In addition, when application list is empty when have to show it. Otherwise
    no loading indicator is visible when page is loaded. */
    return !isSorting && !isFiltering && customerList.length > 0;
  };

  return (
    <S.Container>
      <CustomerFilter />
      <S.Body>
        <S.List>
          {error && error !== 'noCustomerFound' && <S.Error>{intl.formatMessage(messages[error])}</S.Error>}
          <ActionSection />
          {Array.isArray(customerList) && !isAppConfigLoading && <CustomerDataTable />}
          <S.LoadingIcon isLoading={isLoadingIconVisible()} />
        </S.List>
      </S.Body>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    align-items: stretch;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
  `,
  Body: styled.div`
    display: flex;
    flex-grow: 1;
    padding-bottom: 30px;
  `,
  List: styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin: 0 auto;
    max-width: 841px;
    position: relative;
    width: 100%;
  `,
  LoadingIcon: styled(Spinner)<{ isLoading: boolean }>`
    margin-top: 15px;
    margin-bottom: -40px;
    align-self: center;
    ${(props) => (props.isLoading ? '' : 'display: none;')}
  `,
  Error: styled.div`
    margin-top: 10px;
    color: ${(props) => props.theme.legacy.danger};
  `,
};
